import html2canvas from "html2canvas";
import { repairWebmBlob } from "./sdWebmRepair";

export interface CaptureResult {
  ok: true;
  dataUrl: string;
}

export interface CaptureError {
  ok: false;
  reason: string;
}

/**
 * Capture full screen via browser's native getDisplayMedia API.
 * This REQUIRES user permission (browser-enforced).
 * Returns a single frame as a PNG data URL.
 */
export async function captureScreen(maxBytes: number = 800 * 1024): Promise<CaptureResult | CaptureError> {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
    return {
      ok: false,
      reason: "getDisplayMedia not supported in this browser",
    };
  }

  let stream: MediaStream | null = null;
  try {
    // Request screen sharing permission
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: 'always' } as MediaTrackConstraints,
      audio: false,
    });

    const track = stream.getVideoTracks()[0];
    if (!track) {
      return { ok: false, reason: "No video track available" };
    }

    // Use ImageCapture API if available (better quality)
    if ('ImageCapture' in window) {
      const imageCapture = new (window as any).ImageCapture(track);
      const bitmap = await imageCapture.grabFrame();
      
      // 🎯 CRITICAL: Stop stream IMMEDIATELY after capture to prevent window switch
      stream.getTracks().forEach(t => t.stop());
      
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return { ok: false, reason: "Cannot get canvas context" };
      }
      
      ctx.drawImage(bitmap, 0, 0);
      let dataUrl = canvas.toDataURL('image/png');
      
      // Compress if needed
      if (dataUrl.length > maxBytes) {
        const compressed = await compressDataUrl(dataUrl, maxBytes);
        if (!compressed) {
          return { ok: false, reason: "Image exceeds size limit after compression" };
        }
        dataUrl = compressed;
      }
      
      return { ok: true, dataUrl };
    }

    // Fallback: Use video element
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => resolve();
    });
    
    await video.play();
    
    // 🎯 CRITICAL: Capture frame quickly
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      stream.getTracks().forEach(t => t.stop());
      return { ok: false, reason: "Cannot get canvas context" };
    }
    
    ctx.drawImage(video, 0, 0);
    
    // 🎯 CRITICAL: Stop stream IMMEDIATELY after drawing to prevent window switch
    stream.getTracks().forEach(t => t.stop());
    
    let dataUrl = canvas.toDataURL('image/png');
    
    // Compress if needed
    if (dataUrl.length > maxBytes) {
      const compressed = await compressDataUrl(dataUrl, maxBytes);
      if (!compressed) {
        return { ok: false, reason: "Image exceeds size limit after compression" };
      }
      dataUrl = compressed;
    }
    
    return { ok: true, dataUrl };
  } catch (err) {
    if (err instanceof Error) {
      if (err.name === 'NotAllowedError') {
        return { ok: false, reason: "User denied screen sharing permission" };
      }
      return { ok: false, reason: err.message };
    }
    return { ok: false, reason: "Unknown screen capture error" };
  } finally {
    // Always stop the stream
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  }
}

/**
 * Capture a target element (or full document body) as a PNG data URL.
 * Uses html2canvas for reliable CSS rendering across complex layouts.
 *
 * @param target   The DOM element to capture.
 * @param fullPage When true, captures the entire scrollable document body.
 * @param maxBytes Hard cap on the base64 payload size (bytes). The image is
 *                 rendered at progressively lower quality until it fits.
 */
export async function captureViewport(
  target: HTMLElement,
  fullPage: boolean,
  maxBytes: number,
): Promise<CaptureResult | CaptureError> {
  try {
    const captureTarget = fullPage ? document.body : target;

    const canvas = await html2canvas(captureTarget, {
      useCORS: true,
      allowTaint: false,
      logging: false,
      ignoreElements: (el) =>
        el instanceof HTMLElement && el.dataset.screenshotSkip === "true",
      ...(fullPage && {
        windowWidth: document.body.scrollWidth,
        windowHeight: document.body.scrollHeight,
      }),
    });

    let dataUrl = canvas.toDataURL("image/png");

    if (dataUrl.length > maxBytes) {
      const compressedUrl = await compressDataUrl(dataUrl, maxBytes);
      if (!compressedUrl) {
        return { ok: false, reason: "Image exceeds size limit after compression." };
      }
      return { ok: true, dataUrl: compressedUrl };
    }

    return { ok: true, dataUrl };
  } catch (err) {
    return {
      ok: false,
      reason: err instanceof Error ? err.message : "Unknown capture error.",
    };
  }
}

// ─── Screen Recording ─────────────────────────────────────────────────────────

export interface RecordingHandle {
  stop: () => void;
}

/**
 * Start a screen recording using MediaRecorder + getDisplayMedia.
 * User sees a browser permission dialog to choose what to share.
 * When recording stops (via handle.stop() or user ends share),
 * onStop is called with the final Blob and a filename.
 */
export async function startScreenRecording(
  onStop: (blob: Blob, name: string) => void,
): Promise<{ ok: true; handle: RecordingHandle; stream: MediaStream } | CaptureError> {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    return { ok: false, reason: "Screen recording not supported in this browser." };
  }
  if (typeof MediaRecorder === "undefined") {
    return { ok: false, reason: "MediaRecorder not supported in this browser." };
  }

  let stream: MediaStream | null = null;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { cursor: "always" } as MediaTrackConstraints,
      audio: false,
    });

    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : MediaRecorder.isTypeSupported("video/webm")
        ? "video/webm"
        : "";

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: BlobPart[] = [];
    const recordingStartedAt = Date.now();

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      stream?.getTracks().forEach((t) => t.stop());
      const rawBlob = new Blob(chunks, { type: mimeType || "video/webm" });
      const durationMs = Date.now() - recordingStartedAt;
      const name = `recording_${Date.now()}.webm`;
      void repairWebmBlob(rawBlob, durationMs, name).then(fixed =>
        onStop(fixed, name)
      );
    };

    // User stopped sharing from the browser UI
    stream.getVideoTracks()[0].onended = () => {
      if (recorder.state !== "inactive") recorder.stop();
    };

    recorder.start(250); // collect data every 250 ms

    const handle: RecordingHandle = {
      stop: () => {
        if (recorder.state === "recording") {
          recorder.requestData();
          recorder.stop();
        } else {
          stream?.getTracks().forEach((t) => t.stop());
        }
      },
    };

    return { ok: true, handle, stream };
  } catch (err) {
    stream?.getTracks().forEach((t) => t.stop());
    if (err instanceof Error) {
      if (err.name === "NotAllowedError") {
        return { ok: false, reason: "User denied screen recording permission." };
      }
      return { ok: false, reason: err.message };
    }
    return { ok: false, reason: "Unknown recording error." };
  }
}

/** Downsample a PNG data URL until its length is within maxBytes. */
async function compressDataUrl(
  dataUrl: string,
  maxBytes: number,
): Promise<string | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return resolve(null);

      // Step 1: Try JPEG at full resolution with decreasing quality (no dimension loss)
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      const qualitySteps = [0.95, 0.90, 0.85, 0.80, 0.70, 0.60];
      for (const q of qualitySteps) {
        const result = canvas.toDataURL("image/jpeg", q);
        if (result.length <= maxBytes) {
          return resolve(result);
        }
      }

      // Step 2: Scale down dimensions only if JPEG quality steps weren't enough
      let scale = 0.9;
      const attemptScale = () => {
        if (scale <= 0.1) return resolve(null);
        canvas.width = Math.floor(img.naturalWidth * scale);
        canvas.height = Math.floor(img.naturalHeight * scale);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const result = canvas.toDataURL("image/jpeg", 0.80);
        if (result.length <= maxBytes) {
          resolve(result);
        } else {
          scale -= 0.1;
          attemptScale();
        }
      };
      attemptScale();
    };
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}
